/**
 * refetchBus: AI アクション完了等のイベントを契機に、購読側の再フェッチを発火するための軽量 pub/sub。
 *
 * キー命名規約 (全画面共通):
 *   `${entity}:${id}` 例) "property:123", "contract:456"
 *
 * 設計原則:
 * - エンティティ名は知らない。emit 側も subscribe 側も呼び出し時に文字列で指定するだけ。
 * - 1 listener が複数キーを購読したい時は subscribe を複数回呼ぶ。
 * - listener 内の例外は他 listener を巻き込まないように catch する。
 */

type Listener = () => void;

const listeners = new Map<string, Set<Listener>>();

export const buildRefetchKey = (entity: string, id: string | number): string =>
  `${entity}:${id}`;

export const refetchBus = {
  subscribe(key: string, listener: Listener): () => void {
    let set = listeners.get(key);
    if (!set) {
      set = new Set();
      listeners.set(key, set);
    }
    set.add(listener);
    return () => {
      const current = listeners.get(key);
      if (!current) return;
      current.delete(listener);
      if (current.size === 0) listeners.delete(key);
    };
  },

  emit(key: string): void {
    const set = listeners.get(key);
    if (!set) return;
    set.forEach((listener) => {
      try {
        listener();
      } catch (e) {
        // 1 listener の失敗で他を止めない
        // eslint-disable-next-line no-console
        console.error(`[refetchBus] listener for "${key}" threw:`, e);
      }
    });
  },

  /** テスト/デバッグ用。本番コードから呼ばないこと */
  _clearAll(): void {
    listeners.clear();
  },
};
